import React, { useState, useEffect } from "react";
import { Check, Star, ArrowRight, Users, Heart, Instagram } from "lucide-react";
import { toast } from "sonner";
import Logo from "../Logo";
import { brand } from "../../brand";
import { pricing } from "../../data/mock";
import api from "../../lib/api";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import StoreBadges from "./StoreBadges";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.2 1.6 3.8 3.9 4v2.6c-1.4.1-2.7-.3-3.9-1v6.1c0 3.6-2.6 5.9-5.7 5.9-3 0-5.3-2.4-5.3-5.3 0-3.1 2.5-5.3 5.6-5.1v2.7c-.4-.1-.9-.2-1.3-.1-1.3.1-2.3 1.2-2.2 2.5.1 1.3 1.1 2.3 2.4 2.3 1.4 0 2.4-1.1 2.4-2.6V3h3.1z" />
  </svg>
);


export function FamilyPlan({ onJoin }) {
  return (
    <section className="relative mx-auto max-w-6xl px-5 py-16 lg:px-8">
      <div className="glass relative overflow-hidden rounded-[2rem] p-8 sm:p-14">
        <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-sage/15 blur-[120px]" />
        <div className="relative grid items-center gap-10 md:grid-cols-2">
          <div>
            <p className="eyebrow">Your family. One weave.</p>
            <h2 className="mt-5 text-3xl font-semibold leading-tight text-mist sm:text-4xl">
              When you train together, you win together.
            </h2>
            <p className="mt-5 leading-relaxed text-mist-muted">
              Bring your partner and your first child at no extra cost. Share streaks, celebrate every
              win, and get a gentle nudge when someone's thread goes quiet — privacy always respected,
              never silently added. The family plan isn't a billing feature. It's a belief: your wins
              are bigger when they're shared.
            </p>
            <button onClick={onJoin} className="btn-primary mt-8 inline-flex items-center gap-2">
              Start your family plan <ArrowRight size={16} />
            </button>
          </div>
          <div className="space-y-4">
            {[
              { icon: Users, t: "You + partner + 1 child", d: "Included in every family plan" },
              { icon: Heart, t: "Gentle accountability", d: "Alerts when someone needs a nudge" },
              { icon: Check, t: "Extra children $2.99/mo", d: "First child is always free" },
            ].map((r) => (
              <div key={r.t} className="flex items-start gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sage/15 text-sage">
                  <r.icon size={18} />
                </div>
                <div>
                  <div className="font-semibold text-mist">{r.t}</div>
                  <div className="text-sm text-mist-muted">{r.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Pricing({ onJoin }) {
  return (
    <section id="pricing" className="relative mx-auto max-w-5xl px-5 py-28 lg:px-8">
      <div className="mb-14 text-center">
        <p className="eyebrow">Simple, honest pricing</p>
        <h2 className="mt-5 text-3xl font-semibold text-mist sm:text-5xl">Start free. Stay because it works.</h2>
        <p className="mx-auto mt-5 max-w-xl text-mist-muted">30-day free trial on every plan. No card required to begin.</p>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {pricing.map((p) => (
          <div
            key={p.name}
            data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            className={`relative flex flex-col rounded-[2rem] p-7 transition-transform duration-300 hover:-translate-y-1.5 ${
              p.highlight
                ? "border border-brand/40 bg-gradient-to-b from-brand/[0.12] to-transparent"
                : "glass"
            }`}
          >
            {p.highlight && (
              <span className="absolute right-6 top-6 rounded-full bg-brand px-3 py-1 text-xs font-semibold text-[#05101f]">
                Most loved
              </span>
            )}
            <h3 className="font-display text-2xl font-semibold text-mist">{p.name}</h3>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-display text-4xl font-bold text-mist">{p.price}</span>
              <span className="mb-2 text-mist-muted">{p.period}</span>
            </div>
            <p className="mt-3 text-sm text-mist-muted">{p.tagline}</p>
            <button onClick={onJoin} className={`mt-7 w-full ${p.highlight ? "btn-primary" : "btn-ghost"}`}>
              {p.cta || "Start 30-day trial"}
            </button>
            <ul className="mt-8 space-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-mist">
                  <Check size={17} className="mt-0.5 shrink-0 text-brand" />
                  <span className="text-mist-muted">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ReviewDialog({ onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(5);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({ name: "", location: "", text: "" });
  const [busy, setBusy] = useState(false);
  const valid = form.name.trim() && form.text.trim().length >= 8;

  const submit = async () => {
    if (!valid) { toast.error("Add your name and a short review (8+ characters)."); return; }
    setBusy(true);
    try {
      await api.submitReview({ name: form.name.trim(), location: form.location.trim(), stars, text: form.text.trim() });
      toast.success("Thank you! Your review is live.");
      setOpen(false);
      setForm({ name: "", location: "", text: "" }); setStars(5);
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(typeof e?.message === "string" ? e.message : "Could not submit review.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="leave-review-btn">
          <Star size={15} className="fill-current" /> Leave a review
        </button>
      </DialogTrigger>
      <DialogContent className="border border-white/10 bg-ink-surface text-mist sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Share your WYVN story</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} type="button" onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)}
                onClick={() => setStars(s)} data-testid={`review-star-${s}`} aria-label={`${s} stars`}>
                <Star size={26} className={`transition-colors ${s <= (hover || stars) ? "fill-gold text-gold" : "text-mist-muted/40"}`} />
              </button>
            ))}
          </div>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Your name" data-testid="review-name-input"
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-mist placeholder:text-mist-muted focus:border-brand/50 focus:outline-none" />
          <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="City, State (optional)" data-testid="review-location-input"
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-mist placeholder:text-mist-muted focus:border-brand/50 focus:outline-none" />
          <textarea value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
            rows={4} placeholder="What do you love about WYVN?" data-testid="review-text-input"
            className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-mist placeholder:text-mist-muted focus:border-brand/50 focus:outline-none" />
          <button onClick={submit} disabled={busy || !valid} data-testid="review-submit-btn"
            className={`w-full btn-primary ${busy || !valid ? "opacity-50" : ""}`}>
            {busy ? "Posting…" : "Post review"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);
  const load = () => api.reviews().then((r) => setReviews(Array.isArray(r) ? r : [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const count = reviews.length;
  const avg = count ? (reviews.reduce((s, r) => s + (r.stars || 0), 0) / count) : 0;

  // Auto-advance the carousel (pauses nothing heavy — pure state).
  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 6000);
    return () => clearInterval(t);
  }, [count]);
  const go = (n) => setIdx((n + count) % count);

  return (
    <section id="reviews" className="relative mx-auto max-w-4xl px-5 py-16 lg:px-8">
      <div className="mb-12 flex flex-col items-center gap-5 text-center">
        <div>
          <p className="eyebrow">From our members</p>
          <h2 className="mt-5 text-3xl font-semibold text-mist sm:text-4xl">Real stories from the weave</h2>
          {count > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-sm text-mist" data-testid="reviews-counter">
              <span className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className={i < Math.round(avg) ? "fill-gold text-gold" : "text-mist-muted/40"} />
                ))}
              </span>
              <span className="font-semibold">{avg.toFixed(1)}</span>
              <span className="text-mist-muted">· {count} {count === 1 ? "review" : "reviews"}</span>
            </div>
          )}
        </div>
        <ReviewDialog onSubmitted={load} />
      </div>

      {count > 0 && (
        <div className="relative" data-testid="reviews-carousel">
          <div className="glass relative min-h-[240px] overflow-hidden rounded-[2rem] p-8 sm:p-12">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand/12 blur-[90px]" />
            {reviews.map((r, i) => (
              <div key={r.name + i} data-testid={`review-slide-${i}`}
                className={`transition-all duration-500 ${i === idx ? "relative opacity-100" : "pointer-events-none absolute inset-0 p-8 opacity-0 sm:p-12"}`}
                aria-hidden={i !== idx}>
                <div className="mb-5 flex gap-0.5">
                  {Array.from({ length: r.stars }).map((_, j) => (<Star key={j} size={17} className="fill-gold text-gold" />))}
                </div>
                <p className="text-lg leading-relaxed text-mist">“{r.text}”</p>
                <div className="mt-6">
                  <div className="text-sm font-semibold text-mist">{r.name}</div>
                  <div className="text-xs text-mist-muted">{r.location}</div>
                </div>
              </div>
            ))}
          </div>

          {count > 1 && (
            <>
              <div className="mt-6 flex items-center justify-center gap-3">
                <button onClick={() => go(idx - 1)} data-testid="reviews-prev" aria-label="Previous review"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-mist-muted transition-colors hover:border-brand/40 hover:text-mist">
                  <ArrowRight size={16} className="rotate-180" />
                </button>
                <div className="flex gap-1.5">
                  {reviews.map((_, i) => (
                    <button key={i} onClick={() => setIdx(i)} data-testid={`reviews-dot-${i}`} aria-label={`Go to review ${i + 1}`}
                      className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-brand" : "w-2 bg-white/15 hover:bg-white/30"}`} />
                  ))}
                </div>
                <button onClick={() => go(idx + 1)} data-testid="reviews-next" aria-label="Next review"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-mist-muted transition-colors hover:border-brand/40 hover:text-mist">
                  <ArrowRight size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

export function FinalCTA({ onJoin }) {
  return (
    <section className="relative mx-auto max-w-5xl px-5 py-28 lg:px-8">
      <div className="glass relative overflow-hidden rounded-[2.5rem] px-8 py-20 text-center">
        <div className="pointer-events-none absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-brand/20 blur-[120px]" />
        <div className="relative">
          <p className="eyebrow">Every win, woven together</p>
          <h2 className="mx-auto mt-5 max-w-2xl text-3xl font-semibold leading-tight text-mist sm:text-5xl">
            Your win is waiting. Your thread is ready.
          </h2>
          <button onClick={onJoin} className="btn-primary mx-auto mt-9 inline-flex items-center gap-2 text-base">
            Get {brand.name} <ArrowRight size={17} />
          </button>
          <p className="mt-4 text-xs text-mist-muted">30 days free · No card needed · getwyvn.com</p>
          <StoreBadges className="mt-7" />
        </div>
      </div>
    </section>
  );
}

export function Footer({ onNavigate }) {
  const go = (target) => (e) => { e.preventDefault(); onNavigate && onNavigate(target); };
  const LINKS = {
    "The App": { href: "#app" }, "Pricing": { href: "#pricing" }, "Family Plan": { href: "#pricing" },
    "AI Coaches": { href: "#app" }, "Mission": { href: "#mission" }, "Members": { href: "#map" },
    "Stories": { href: "#reviews" }, "Contact": { nav: "contact" },
    "Privacy": { nav: "privacy" }, "Terms": { nav: "terms" }, "Medical Disclaimer": { nav: "disclaimer" },
  };
  return (
    <footer className="border-t border-white/[0.06] px-5 py-14 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col items-start justify-between gap-10 md:flex-row">
          <div className="max-w-xs">
            <Logo size={30} />
            <p className="mt-4 text-sm leading-relaxed text-mist-muted">{brand.tagline}. {brand.subline}</p>
            <a
              href={brand.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-mist-muted transition-colors hover:border-brand/40 hover:text-mist"
            >
              <Instagram size={16} /> {brand.social.instagramHandle}
            </a>
            <a
              href={brand.social.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-tiktok-link"
              className="mt-3 ml-0 md:ml-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-mist-muted transition-colors hover:border-brand/40 hover:text-mist"
            >
              <TikTokIcon size={15} /> {brand.social.tiktokHandle}
            </a>
          </div>
          <div className="grid grid-cols-2 gap-x-16 gap-y-6 sm:grid-cols-3">
            {[
              { h: "Product", items: ["The App", "Pricing", "Family Plan", "AI Coaches"] },
              { h: "Company", items: ["Mission", "Members", "Stories", "Contact"] },
              { h: "Legal", items: ["Privacy", "Terms", "Medical Disclaimer"] },
            ].map((col) => (
              <div key={col.h}>
                <div className="mb-4 text-xs font-semibold uppercase tracking-wider text-mist">{col.h}</div>
                <ul className="space-y-3">
                  {col.items.map((it) => {
                    const link = LINKS[it] || { href: "#" };
                    return (
                      <li key={it}>
                        <a
                          href={link.nav ? `#${link.nav}` : link.href}
                          onClick={link.nav ? go(link.nav) : undefined}
                          data-testid={`footer-link-${it.toLowerCase().replace(/\s+/g, "-")}`}
                          className="text-sm text-mist-muted transition-colors hover:text-mist"
                        >
                          {it}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <nav aria-label="Explore WYVN" className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 border-t border-white/[0.06] pt-8 text-xs text-mist-muted">
          {[
            ["The App", "#app"], ["AI Coaches", "#app"], ["The Weave", "#woven"],
            ["Mission", "#mission"], ["Members", "#map"], ["Stories", "#reviews"], ["Pricing", "#pricing"],
          ].map(([label, href]) => (
            <a
              key={label}
              href={href}
              data-testid={`sitemap-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="transition-colors hover:text-mist"
            >
              {label}
            </a>
          ))}
        </nav>
        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/[0.06] pt-8 text-xs text-mist-muted sm:flex-row">
          <span>© {new Date().getFullYear()} {brand.name}. All rights reserved.</span>
          <span>{brand.email} · Get WYVN at {brand.site}</span>
        </div>
      </div>
    </footer>
  );
}
